import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CaError, CaFormFieldComponent, CaHint, CaLabel } from '@global-front-components/ui/form-field';
import { CaInputDirective } from '@global-front-components/ui/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CuentaCorriente, MedioPago } from '../../model/poliza.model';
import { TipoCuenta } from '../../util/enum/tipo-cuenta';
import { TipoMedioPago } from '../../util/enum/tipo-medio-pago';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MedioPagoCatalogo, TipoCuentaCatalogo } from '../../model/catalogo.model';
import { CaIbanValidator } from '@global-front-components/common/validator-iban';
import { CaSelectComponent } from '@global-front-components/ui/select';


@Component({
	selector: 'app-datos-pago',
	standalone: true,
	imports: [
		CommonModule,
		CaLabel,
		CaInputDirective,
		CaFormFieldComponent,
		TranslateModule,
		CaSelectComponent,
		CaError,
		ReactiveFormsModule,
		CaHint
	], templateUrl: './datos-pago.component.html',
	styleUrls: ['./datos-pago.component.scss']
})
export class DatosPagoComponent implements OnInit {
	public TipoMedioPago = TipoMedioPago;
	public TipoCuenta = TipoCuenta;
	@Input() cuenta: CuentaCorriente;
	@Input() mediosPago!: MedioPagoCatalogo[];
	@Input() form!: FormGroup;
	@Input() editable: boolean = true;
	@Output() medioPagoChange = new EventEmitter<MedioPago>();


	medioPagoSeleccionado!: string;
	tipoCuentaSeleccionada!: string;
	tiposCuenta: TipoCuentaCatalogo[] = [];

	constructor(
		private fb: FormBuilder,
		private translateService: TranslateService
	) { }

	ngOnInit(): void {
		if (!this.form) {
			this.form = this.fb.group({});
		}
		this.tiposCuenta.push({ codigoTipoCuenta: this.TipoCuenta.CCC, descTipoCuenta: this.translateService.instant('CATALOGO.DATOS_PAGO.TIPO_CUENTA.CCC') });
		this.tiposCuenta.push({ codigoTipoCuenta: this.TipoCuenta.IBAN, descTipoCuenta: this.translateService.instant('CATALOGO.DATOS_PAGO.TIPO_CUENTA.IBAN') });

		if (this.cuenta !== null) {
			this.medioPagoSeleccionado = this.TipoMedioPago.CUENTA_BANCARIA;

			if (this.cuenta?.codigoIban) {
				this.form.addControl('medio-pago', this.fb.control(this.TipoCuenta.IBAN, [Validators.required]));
				this.tipoCuentaSeleccionada = this.TipoCuenta.IBAN;
				this.anadirControlIBAN(this.cuenta);
			} else {
				this.form.addControl('medio-pago', this.fb.control(this.TipoCuenta.CCC, [Validators.required]));
				this.tipoCuentaSeleccionada = this.TipoCuenta.CCC;
				this.anadirControlCCC(this.cuenta);
			}
			this.form.addControl('tipo-cuenta', this.fb.control({ value: this.tipoCuentaSeleccionada, disabled: !this.editable }));
		} else {
			this.buildForm();
		}

		this.form.valueChanges.subscribe(() => {
			this.emitirMedioPago();
		});
	}

	private buildForm() {
		const medioPagoDefecto = this.mediosPago?.find(() => true).codigoMedioPago;
		if (medioPagoDefecto) {
			this.medioPagoSeleccionado = medioPagoDefecto;

			this.tipoCuentaSeleccionada = TipoCuenta.CCC;

			this.form.addControl('medio-pago', this.fb.control(this.medioPagoSeleccionado, [Validators.required]));
			this.manejarControlesMedioPago();

			this.form.get('medio-pago')?.valueChanges.subscribe((value: string) => {
				this.medioPagoSeleccionado = value;
				this.manejarControlesMedioPago();
			});

			this.form.get('tipo-cuenta')?.valueChanges.subscribe((value: string) => {
				this.tipoCuentaSeleccionada = value;
				if (this.tipoCuentaSeleccionada === this.TipoCuenta.CCC) {
					this.anadirControlCCC(null);
				} else {
					this.anadirControlIBAN(null);
				}
			});
		}
	}

	private manejarControlesMedioPago(): void {
		if (this.medioPagoSeleccionado === this.TipoMedioPago.CUENTA_BANCARIA) {
			this.form.addControl('tipo-cuenta', this.fb.control({ value: this.tipoCuentaSeleccionada, disabled: !this.editable }));
			this.anadirControlCCC(null);
		} else {
			this.eliminarControlesCuentaBancaria();
		}
	}

	private anadirControlCCC(cuenta: CuentaCorriente): void {
		this.form.addControl('datos-pago-entidad', this.fb.control({ value: cuenta?.codigoEntidad ?? '', disabled: !this.editable }, [Validators.required]));
		this.form.addControl('datos-pago-sucursal', this.fb.control({ value: cuenta?.codigoOficina ?? '', disabled: !this.editable }, [Validators.required]));
		this.form.addControl('datos-pago-dc', this.fb.control({ value: cuenta?.digitoControl ?? '', disabled: !this.editable }, [Validators.required]));
		this.form.addControl('datos-pago-cuenta', this.fb.control({ value: cuenta?.numeroCuenta ?? '', disabled: !this.editable }, [Validators.required]));
		this.form.removeControl('datos-pago-IBAN');
	}

	private anadirControlIBAN(cuenta: CuentaCorriente): void {
		this.form.addControl('datos-pago-IBAN', this.fb.control({ value: cuenta?.codigoIban ?? '', disabled: !this.editable },
			 [Validators.required, CaIbanValidator('invalidIban')]));
		this.form.removeControl('datos-pago-entidad');
		this.form.removeControl('datos-pago-sucursal');
		this.form.removeControl('datos-pago-dc');
		this.form.removeControl('datos-pago-cuenta');
	}

	private eliminarControlesCuentaBancaria(): void {
		this.form.removeControl('datos-pago-IBAN');
		this.form.removeControl('datos-pago-entidad');
		this.form.removeControl('datos-pago-sucursal');
		this.form.removeControl('datos-pago-dc');
		this.form.removeControl('datos-pago-cuenta');
	}

	private getMedioPago(): MedioPago {
		const valorMp = this.form.get('medio-pago')?.value;
		if (valorMp !== TipoMedioPago.CUENTA_BANCARIA) {
			// Si el medio de pago no es cuenta corriente no rellenamos
			return null;
		}
		const cuenta: CuentaCorriente = this.getCuentaCorriente();
		const medioPago: MedioPago = { cuentaBancaria: cuenta };
		return medioPago;
	}

	private getCuentaCorriente(): CuentaCorriente {
		const valorTc = this.form.get('tipo-cuenta')?.value;
		let cuenta: CuentaCorriente = {};

		if (valorTc === TipoCuenta.CCC) {
			cuenta.codigoEntidad = this.form.get('datos-pago-entidad')?.value;
			cuenta.codigoOficina = this.form.get('datos-pago-sucursal')?.value;
			cuenta.digitoControl = this.form.get('datos-pago-dc')?.value;
			cuenta.numeroCuenta = this.form.get('datos-pago-cuenta')?.value;
		} else {
			cuenta.codigoIban = this.form.get('datos-pago-IBAN')?.value;
		}
		return cuenta;
	}

	emitirMedioPago(): void {
		this.medioPagoChange.emit(this.getMedioPago());
	}
}
